import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, Mail, ArrowLeft, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      // Endpoint always returns 200 whether or not the email is registered
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      // Even on network error we show the same generic success — avoids enumeration
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-linen">
      <div className="w-full max-w-md">
        <div className="mb-6">
          <Link to="/login" data-testid="link-back-login"
                className="text-sm text-ink-mid hover:text-ink inline-flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Back to sign in
          </Link>
        </div>

        {!sent ? (
          <form onSubmit={submit} className="card-widget p-8" data-testid="forgot-password-form">
            <div className="w-12 h-12 rounded-xl bg-pine text-linen grid place-items-center mb-5">
              <Mail className="w-5 h-5" />
            </div>
            <h1 className="font-display text-3xl text-ink leading-tight">Reset your password</h1>
            <p className="mt-3 text-sm text-ink-mid leading-relaxed">
              Enter the email you use for Viyora and we'll send you a link to set a new password.
              The link is valid for 30 minutes.
            </p>

            <label className="block mt-6">
              <span className="text-xs uppercase tracking-widest text-ink-mid">Email</span>
              <input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                required autoFocus autoComplete="email"
                data-testid="forgot-password-email"
                placeholder="you@clinic.com"
                className="w-full mt-1 rounded-xl border border-[#E8EAE9] bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-pine/40"
              />
            </label>

            <button
              type="submit" disabled={loading}
              data-testid="forgot-password-submit"
              className="mt-6 w-full btn-primary flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Send reset link
            </button>
          </form>
        ) : (
          <div className="card-widget p-8 text-center" data-testid="forgot-password-sent">
            <div className="w-14 h-14 rounded-full bg-pine/10 text-ink grid place-items-center mx-auto mb-4">
              <CheckCircle2 className="w-7 h-7" />
            </div>
            <h1 className="font-display text-2xl text-ink">Check your email</h1>
            <p className="mt-3 text-sm text-ink-mid leading-relaxed">
              If an account exists for <b className="text-ink">{email}</b>, we've sent a link to reset the password.
              The link is valid for 30 minutes. If you don't see it in a few minutes, check your spam folder.
            </p>
            <Link to="/login" data-testid="link-back-login-2"
                  className="inline-block mt-6 text-sm text-ink font-medium hover:underline">
              Back to sign in
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
